// src/components/ExerciseItem.js
import React, { useState } from 'react';
import { bodyAreaOptions } from '../data/options';

function ExerciseItem({ exercise, index, currentIndex, restTime }) {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const toggleExpand = () => {
    setIsExpanded(!isExpanded);
  };
  
  // Get appropriate background color based on difficulty
  const getDifficultyColor = (difficulty) => {
    switch(difficulty) {
      case 'beginner':
        return 'bg-green-100 text-green-800';
      case 'intermediate':
        return 'bg-blue-100 text-blue-800';
      case 'advanced':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };
  
  // Get icon based on equipment
  const getEquipmentIcon = (equipment) => {
    if (!equipment || equipment.length === 0) return '🧘‍♀️';
    
    const primaryEquipment = equipment[0];
    switch(primaryEquipment) {
      case 'reformer': return '🛌';
      case 'chair': return '🪑';
      case 'tower': return '🏗️';
      case 'ball': return '⚽';
      case 'band': return '➰';
      case 'ring': return '⭕';
      case 'weights': return '🏋️‍♀️';
      case 'foam_roller': return '📏';
      default: return '🧘‍♀️';
    }
  };
  
  const isCurrent = currentIndex === index;
  
  return (
    <div 
      className={`rounded-lg shadow-sm border transition-all duration-300 ${
        isCurrent ? 'bg-blue-50 border-blue-300' : 'bg-white border-gray-200 hover:border-blue-200'
      }`}
    >
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex items-center">
            <div className={`flex items-center justify-center h-10 w-10 rounded-full ${
              isCurrent ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-700'
            } mr-3 text-lg`}>
              {index + 1}
            </div>
            <div>
              <h3 className="font-medium text-lg flex items-center">
                <span className="mr-2">{exercise.image || getEquipmentIcon(exercise.equipment)}</span>
                {exercise.name}
              </h3>
              <div className="text-gray-500 text-sm flex items-center mt-1">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {exercise.duration} min
                <span className="mx-2">•</span>
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {restTime}s rest
              </div>
            </div>
          </div>
          
          <button 
            onClick={toggleExpand}
            className={`p-1 rounded-full transition-colors ${
              isExpanded ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
            }`}
            aria-label={isExpanded ? "Collapse exercise details" : "Expand exercise details"}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className={`h-5 w-5 transition-transform ${isExpanded ? 'transform rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        </div>
        
        <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="px-4 pt-2 pb-4 border-t border-gray-100 mt-2">
            <p className="text-gray-700 mb-4">{exercise.description}</p>
            
            {exercise.relevantModifications && exercise.relevantModifications.length > 0 && (
              <div className="mt-3 p-3 bg-amber-50 rounded-md">
                <p className="text-sm font-medium text-amber-800 mb-2">Modifications:</p>
                <ul className="text-sm text-amber-700 space-y-1.5">
                  {exercise.relevantModifications.map((mod, i) => (
                    <li key={i} className="flex items-start">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-1.5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <span><strong>{mod.condition}:</strong> {mod.instruction}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            
            <div className="mt-4 flex flex-wrap gap-2">
              {exercise.targetAreas.map(area => {
                const areaOption = bodyAreaOptions.find(option => option.id === area);
                return (
                  <span key={area} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                    {areaOption ? areaOption.label : area}
                  </span>
                );
              })}
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDifficultyColor(exercise.difficulty)}`}>
                {exercise.difficulty.charAt(0).toUpperCase() + exercise.difficulty.slice(1)}
              </span>
              {exercise.equipment && exercise.equipment.map(eq => (
                eq !== 'none' && (
                  <span key={eq} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                    {eq.charAt(0).toUpperCase() + eq.slice(1).replace('_', ' ')}
                  </span>
                )
              ))}
            </div>
          </div>
        </div>
        
        {isCurrent && (
          <div className="bg-blue-100 text-blue-800 px-4 py-2 text-xs font-medium rounded-b-lg">
            CURRENT EXERCISE
          </div>
        )}
      </div>
    </div>
  );
}

export default ExerciseItem;